import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import { log } from '@quoruum/frontend-common/logger';
import { hotelActionAddProblemMutation } from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { HotelDataIssueBeanWritable } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import {
  hotelGetOptions,
  hotelSaveMutation,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type {
  CityBean,
  HotelBean,
  HotelBeanWritable,
} from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import { useMutation } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import { DeleteConfirmationModal } from '../../components/DeleteConfirmationModal';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../../components/EditableEntityTable';
import { EditableEntityTable } from '../../components/EditableEntityTable';
import { IssueListTable } from '../../components/IssueListTable';
import {
  BOOKING_ENGINE_TYPE_OPTIONS,
  type BookingEngineType,
} from '../../constants/bookingEngines';
import { EditableHotelFieldKey, formatUnknownValue, isObjectRecord } from './hotelMapShared';

type EnqueueCenterJobType = 'rateShop' | 'learnRateShop';

type EditableHotelFieldDefinition = {
  key: EditableHotelFieldKey;
  label: string;
  editSpec: EditableFieldSpec;
  getValue: (hotel: HotelBean) => string;
  renderReadOnlyValue: (value: string) => ReactNode;
  buildMutationBody: (
    hotelId: string,
    value: string
  ) => Pick<
    HotelBeanWritable,
    'id' | 'name' | 'homePageUri' | 'bookingEngineType' | 'currencyCode'
  >;
};

type HomePageIssuesRenderOptions = {
  onClearIssueClick: (issue: unknown) => void;
};
const HOTEL_DISPLAY_KEYS: Array<keyof HotelBean> = [
  'id',
  'name',
  'bookingEngineType',
  'status',
  'homePageUri',
  'homePageIssues',
  'address1',
  'address2',
  'city',
  'postalCode',
  'phone',
  'airportCode',
  'latitude',
  'longitude',
  'currencyCode',
  'reviewScore',
  'numberOfReviews',
  'starRating',
  'brand',
  'externalIds',
  'fieldsToClear',
  'auditBean',
];

function validateBasicHttpUrl(value: string): string | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'URL must start with http:// or https://.';
    }

    if (!parsed.hostname) {
      return 'URL must include a hostname.';
    }

    return null;
  } catch {
    return 'Enter a valid URL.';
  }
}

const EDITABLE_HOTEL_FIELD_DEFINITIONS: Array<EditableHotelFieldDefinition> = [
  {
    key: 'name',
    label: 'name',
    editSpec: { editType: 'text' },
    getValue: (hotel) => (typeof hotel.name === 'string' ? hotel.name : ''),
    renderReadOnlyValue: (value) => value || '-',
    buildMutationBody: (hotelId, value) => ({
      id: hotelId,
      name: value,
    }),
  },
  {
    key: 'homePageUri',
    label: 'homePageUri',
    editSpec: { editType: 'text', validateValue: validateBasicHttpUrl },
    getValue: (hotel) => (typeof hotel.homePageUri === 'string' ? hotel.homePageUri : ''),
    renderReadOnlyValue: (value) => {
      const trimmedValue = value.trim();
      if (!trimmedValue) {
        return '-';
      }

      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-700 underline hover:text-sky-800"
        >
          {value}
        </a>
      );
    },
    buildMutationBody: (hotelId, value) => ({
      id: hotelId,
      homePageUri: value,
    }),
  },
  {
    key: 'bookingEngineType',
    label: 'bookingEngineType',
    editSpec: {
      editType: 'select',
      options: BOOKING_ENGINE_TYPE_OPTIONS,
      allowEmptyOption: true,
      emptyOptionLabel: '-',
    },
    getValue: (hotel) =>
      typeof hotel.bookingEngineType === 'string' ? hotel.bookingEngineType : '',
    renderReadOnlyValue: (value) => value || '-',
    buildMutationBody: (hotelId, value) => ({
      id: hotelId,
      bookingEngineType: value
        ? (value as BookingEngineType)
        : (null as unknown as BookingEngineType),
    }),
  },
  {
    key: 'currencyCode',
    label: 'currencyCode',
    editSpec: { editType: 'text' as const },
    getValue: (hotel) => (typeof hotel.currencyCode === 'string' ? hotel.currencyCode : ''),
    renderReadOnlyValue: (value) => value || '-',
    buildMutationBody: (hotelId, value) => ({
      id: hotelId,
      currencyCode: value || undefined,
    }),
  },
];

const EDITABLE_HOTEL_FIELD_BY_KEY = new Map(
  EDITABLE_HOTEL_FIELD_DEFINITIONS.map((field) => [field.key, field] as const)
);

const HOTEL_EDITABLE_FIELD_SPECS: ReadonlyMap<EditableHotelFieldKey, EditableFieldSpec> = new Map(
  EDITABLE_HOTEL_FIELD_DEFINITIONS.map((def) => [def.key, def.editSpec])
);

function buildHomePageIssueBody(
  issueBody: Record<string, unknown>,
  hotelId: string
): HotelDataIssueBeanWritable {
  const body: HotelDataIssueBeanWritable = {
    id: hotelId,
    field: 'HOME_PAGE',
    deleted: true,
  };

  if (typeof issueBody.reason === 'string') {
    body.reason = issueBody.reason;
  }
  if (issueBody.source === 'DECORATOR' || issueBody.source === 'LAMBDA') {
    body.source = issueBody.source;
  }
  if (issueBody.status === 'RECOVERABLE' || issueBody.status === 'UNRECOVERABLE') {
    body.status = issueBody.status;
  }
  if (typeof issueBody.timestamp === 'string') {
    body.timestamp = issueBody.timestamp;
  }
  if (Object.prototype.hasOwnProperty.call(issueBody, 'value')) {
    body.value = issueBody.value;
  }

  return body;
}

function buildEnqueueCenterLink(hotel: HotelBean, jobType: EnqueueCenterJobType): string {
  const searchParams = new URLSearchParams({
    singleJobType: jobType,
    hotelId: hotel.id ?? '',
  });

  return `/tools/enqueue-center?${searchParams.toString()}`;
}

function buildHotelDetailsRows(
  hotel: HotelBean,
  homePageIssuesRenderOptions: HomePageIssuesRenderOptions
): Array<EntityDetailRow<EditableHotelFieldKey>> {
  const rows: Array<EntityDetailRow<EditableHotelFieldKey>> = [];
  const includedEditableFields = new Set<EditableHotelFieldKey>();
  const includedHotelKeys = new Set<string>();

  const pushRow = (key: string, value: unknown): void => {
    includedHotelKeys.add(key);

    const editableFieldDefinition = EDITABLE_HOTEL_FIELD_BY_KEY.get(key as EditableHotelFieldKey);
    if (editableFieldDefinition) {
      const editableFieldValue = editableFieldDefinition.getValue(hotel);
      rows.push({
        id: key,
        key: editableFieldDefinition.label,
        value: editableFieldDefinition.renderReadOnlyValue(editableFieldValue),
        editableFieldKey: editableFieldDefinition.key,
        editableFieldValue,
      });
      includedEditableFields.add(editableFieldDefinition.key);
      return;
    }

    if (key === 'homePageIssues') {
      rows.push({
        id: key,
        key,
        value: renderHomePageIssuesValue(value, homePageIssuesRenderOptions),
      });
      return;
    }

    if (key === 'city') {
      rows.push({
        id: key,
        key,
        value: renderHotelCity(value),
      });
      return;
    }

    rows.push({
      id: key,
      key,
      value: formatUnknownValue(value),
    });
  };

  for (const key of HOTEL_DISPLAY_KEYS) {
    pushRow(key, hotel[key]);
  }

  for (const [key, value] of Object.entries(hotel)) {
    if (includedHotelKeys.has(key)) {
      continue;
    }

    pushRow(key, value);
  }

  for (const editableFieldDefinition of EDITABLE_HOTEL_FIELD_DEFINITIONS) {
    if (includedEditableFields.has(editableFieldDefinition.key)) {
      continue;
    }

    const editableFieldValue = editableFieldDefinition.getValue(hotel);
    rows.push({
      id: editableFieldDefinition.key,
      key: editableFieldDefinition.label,
      value: editableFieldDefinition.renderReadOnlyValue(editableFieldValue),
      editableFieldKey: editableFieldDefinition.key,
      editableFieldValue,
    });
  }

  return rows;
}

function renderHomePageIssuesValue(
  value: unknown,
  homePageIssuesRenderOptions: HomePageIssuesRenderOptions
): ReactNode {
  if (!Array.isArray(value)) {
    return formatUnknownValue(value);
  }

  if (value.length === 0) {
    return '-';
  }

  return (
    <IssueListTable
      issues={value}
      onClearIssueClick={homePageIssuesRenderOptions.onClearIssueClick}
      renderIssue={formatUnknownValue}
    />
  );
}

function renderHotelCity(value: unknown): ReactNode {
  if (!value || typeof value !== 'object' || !('id' in value)) {
    return '-';
  }

  const city = value as CityBean;
  if (!city.id) {
    return '-';
  }

  const cityName = city.name ?? 'Unknown';
  const suffixParts = [city.stateCode, city.countryCode].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0
  );
  const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(', ')})` : '';
  const cityLabel = `${cityName}${suffix}`;

  return cityLabel;
}

type HotelSectionProps = {
  selectedHotelDetails: HotelBean;
};

export function HotelSection({ selectedHotelDetails }: HotelSectionProps): ReactElement {
  const selectedHotelId = selectedHotelDetails.id;
  const [isClearIssueModalOpen, setIsClearIssueModalOpen] = useState(false);
  const [clearIssueConfirmation, setClearIssueConfirmation] = useState('');
  const [clearIssueTitle, setClearIssueTitle] = useState('');
  const [selectedIssueBody, setSelectedIssueBody] = useState<Record<string, unknown> | null>(null);
  const saveHotelMutation = useMutation(hotelSaveMutation());
  const clearHomePageIssueMutation = useMutation(hotelActionAddProblemMutation());
  async function handleSaveHotelField(
    fieldKey: EditableHotelFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    const fieldDef = EDITABLE_HOTEL_FIELD_BY_KEY.get(fieldKey);
    if (!fieldDef || !selectedHotelId) return;

    if (typeof value !== 'string') {
      throw new Error('Invalid value for selected field.');
    }

    await saveHotelMutation.mutateAsync({
      path: { id: selectedHotelId },
      body: fieldDef.buildMutationBody(selectedHotelId, value),
    });

    await queryClient.fetchQuery(hotelGetOptions({ path: { id: selectedHotelId } }));
  }
  function handleOpenClearHomePageIssueModal(issue: unknown): void {
    setSelectedIssueBody(isObjectRecord(issue) ? issue : { value: issue });
    setClearIssueTitle('Clear Home Page Issue');
    setClearIssueConfirmation('');
    setIsClearIssueModalOpen(true);
  }
  async function handleClearIssue(): Promise<void> {
    if (!selectedHotelId || !selectedIssueBody) {
      return;
    }

    try {
      await clearHomePageIssueMutation.mutateAsync({
        path: {
          id: selectedHotelId,
        },
        body: buildHomePageIssueBody(selectedIssueBody, selectedHotelId),
      });

      setSelectedIssueBody(null);
      setClearIssueTitle('');
      setClearIssueConfirmation('');
      setIsClearIssueModalOpen(false);

      await queryClient.fetchQuery(hotelGetOptions({ path: { id: selectedHotelId } }));
    } catch (error) {
      log.error('Failed to clear issue:', error);
    }
  }

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto rounded-md border border-slate-200">
        <EditableEntityTable
          key={selectedHotelId}
          rows={buildHotelDetailsRows(selectedHotelDetails, {
            onClearIssueClick: handleOpenClearHomePageIssueModal,
          })}
          fieldDefinitions={HOTEL_EDITABLE_FIELD_SPECS}
          onSave={handleSaveHotelField}
          header={
            selectedHotelId ? (
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                <div className="flex items-center gap-3">
                  <Link
                    to={`/tools/changelog?entityType=Hotel&entityId=${encodeURIComponent(selectedHotelId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sky-700 underline"
                  >
                    Changelog
                  </Link>
                  <Link
                    to={buildEnqueueCenterLink(selectedHotelDetails, 'rateShop')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sky-700 underline"
                  >
                    Rate Shop
                  </Link>
                  <Link
                    to={buildEnqueueCenterLink(selectedHotelDetails, 'learnRateShop')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-sky-700 underline"
                  >
                    Learn Rate Shop
                  </Link>
                </div>
              </div>
            ) : null
          }
        />
      </div>
      <DeleteConfirmationModal
        isOpen={isClearIssueModalOpen}
        onClose={() => {
          setIsClearIssueModalOpen(false);
          setClearIssueConfirmation('');
          setSelectedIssueBody(null);
          setClearIssueTitle('');
        }}
        ariaLabel="Clear issue confirmation"
        title={clearIssueTitle || 'Clear Issue'}
        message="Are you sure?"
        confirmationValue={clearIssueConfirmation}
        onConfirmationChange={setClearIssueConfirmation}
        onSubmit={() => {
          void handleClearIssue();
        }}
        isSubmitting={clearHomePageIssueMutation.isPending}
        isSubmitDisabled={
          clearIssueConfirmation !== 'delete' ||
          !selectedIssueBody ||
          clearHomePageIssueMutation.isPending
        }
      />
    </>
  );
}
